import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import LoginLayout from "../../layouts/LoginLayout";
import AuthPlate from "../../components/auth/AuthPlate";
import CustomModal from "../../components/modal/CustomModal";
import apiClient from "../../api/axios";
import { useAuth } from "../../contexts/UserContext";
import type { OnboardingGateState } from "../../contexts/OrganizationContext";
import SsoService, {
  OnboardingField,
  OnboardingListingStatus,
  OnboardingPayload,
} from "../../service/sso.service";
import { SectorPicker, useSectorOptions } from "../../components/classification/SectorPicker";
import SectorIsicPicker from "../../components/cat15/SectorIsicPicker";
import LabelInput from "../../components/input/LabelInput";
import { LISTING_STATUS_LABEL_KEY, Organization, SectorRef, sectorRefLabel } from "../../models/Organization";
import {
  cacheOnboardingCompleted,
  dismissReminder,
  isOnboardingCompletedCached,
} from "../../utils/onboardingCache";
import secureLocalStorage from "../../utils/secureLocalStorage";
import { STORAGE_KEYS } from "../../constants";
import { User } from "../../models/User";
import { common } from "../../i18n";

type SubmitErrorKind =
  | "SECTOR_INVALID"
  | "NO_MEMBERSHIP"
  | "FORBIDDEN"
  | "ALREADY_COMPLETED"
  | "NOT_BORROWER"
  | "GENERIC";

// A secondary-read gate can wave through what the primary then refuses, and retrying cannot fix that.
const TERMINAL_ERRORS: readonly SubmitErrorKind[] = ["FORBIDDEN", "ALREADY_COMPLETED", "NOT_BORROWER"];

// Keyed as the request fields, so an answer is copied into the payload by the name the server asked for.
type OnboardingFormValues = Record<"sectorIsicCode" | "sectorTsicCode", string> & {
  listingStatus: OnboardingListingStatus | "";
};

type AskedFields = { missing: OnboardingField[]; optional: OnboardingField[] };

type OnboardingErrorData = { code?: string; results?: { already_set?: OnboardingField[] } };

// The order the form asks them, which is also the order a notice names them in.
const ONBOARDING_FIELDS: readonly OnboardingField[] = ["sectorIsicCode", "listingStatus", "sectorTsicCode"];

const FIELD_NAME_KEY: Record<OnboardingField, string> = {
  sectorIsicCode: "Onboarding-Field-Isic",
  listingStatus: "Onboarding-Field-Listing",
  sectorTsicCode: "Onboarding-Field-Tsic",
};

function mapSubmitErrorKind(code: string | undefined): SubmitErrorKind {
  switch (code) {
    case "SSO_ONBOARDING_SECTOR_INVALID":
      return "SECTOR_INVALID";
    case "SSO_ONBOARDING_NO_MEMBERSHIP":
      return "NO_MEMBERSHIP";
    case "SSO_ONBOARDING_FORBIDDEN":
      return "FORBIDDEN";
    case "SSO_ONBOARDING_ALREADY_COMPLETED":
      return "ALREADY_COMPLETED";
    case "SSO_ONBOARDING_NOT_BORROWER":
      return "NOT_BORROWER";
    default:
      return "GENERIC";
  }
}

const ERROR_KEY: Record<SubmitErrorKind, string> = {
  SECTOR_INVALID: "Onboarding-Error-Sector-Invalid",
  NO_MEMBERSHIP: "Onboarding-Error-No-Membership",
  FORBIDDEN: "Onboarding-Error-Forbidden",
  ALREADY_COMPLETED: "Onboarding-Error-Already-Completed",
  NOT_BORROWER: "Onboarding-Error-Not-Borrower",
  GENERIC: "Onboarding-Error-Generic",
};

// Each terminal refusal gets its own heading; one shared title made three different outcomes read as the same failure.
const ERROR_TITLE_KEY: Record<SubmitErrorKind, string> = {
  SECTOR_INVALID: "Onboarding-Title",
  NO_MEMBERSHIP: "Onboarding-Title",
  FORBIDDEN: "Onboarding-Title-Forbidden",
  ALREADY_COMPLETED: "Onboarding-Title-Already-Completed",
  NOT_BORROWER: "Onboarding-Title-Not-Borrower",
  GENERIC: "Onboarding-Title",
};

/** Where to go on the way out. The banner supplies the page it interrupted; a direct visit has none. */
const useExit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const target = (location.state as { from?: string } | null)?.from ?? "/";
  return {
    // replace on success so Back cannot re-enter a wizard that is finished; push on cancel, which leaves it open.
    finish: () => navigate(target, { replace: true }),
    abandon: () => navigate(target, { replace: false }),
  };
};

const ExitCard: React.FC<{ titleKey: string; messageKey: string; severity: "info" | "error" }> = ({
  titleKey,
  messageKey,
  severity,
}) => {
  const { t } = useTranslation([common]);
  const { abandon } = useExit();
  return (
    <AuthPlate>
      <Stack direction="column" spacing={2} alignItems="center" width="100%">
        <Typography variant="h1" textAlign="center">
          {t(titleKey)}
        </Typography>
        <Alert severity={severity} sx={{ width: "100%" }}>
          {t(messageKey)}
        </Alert>
        <Button fullWidth variant="contained" color="secondary" onClick={abandon}>
          {t("Onboarding-Back-To-App")}
        </Button>
      </Stack>
    </AuthPlate>
  );
};

/** A refusal the answer cannot survive: the form behind stays visible, but only the way out is offered. */
const RefusalModal: React.FC<{ kind: SubmitErrorKind; onLeave: () => void }> = ({ kind, onLeave }) => {
  const { t } = useTranslation([common]);
  return (
    <CustomModal
      open
      onClose={onLeave}
      showCloseIcon={false}
      maxWidth="440px"
      header={<Typography variant="h1">{t(ERROR_TITLE_KEY[kind])}</Typography>}
      body={
        <Stack direction="column" spacing={2} width="100%">
          <Typography variant="body1">{t(ERROR_KEY[kind])}</Typography>
          <Button fullWidth variant="contained" color="secondary" onClick={onLeave}>
            {t("Onboarding-Back-To-App")}
          </Button>
        </Stack>
      }
    />
  );
};

// Sending makes the answer the bank's to change, so the borrower sees exactly what will be filed first.
const AnswerCheck: React.FC<{ rows: AnswerRow[]; submitting: boolean; onBack: () => void; onConfirm: () => void }> = ({
  rows,
  submitting,
  onBack,
  onConfirm,
}) => {
  const { t } = useTranslation([common]);
  return (
    <CustomModal
      open
      onClose={onBack}
      showCloseIcon={false}
      maxWidth="440px"
      header={<Typography variant="h1">{t("Onboarding-Confirm-Title")}</Typography>}
      body={
        <Stack direction="column" spacing={2} width="100%">
          <AnswerBox
            rows={rows}
            heading={
              <Typography variant="body2" fontWeight={600} color="var(--gray9)">
                {t("Onboarding-Confirm-Answers")}
              </Typography>
            }
          />
          <Box
            display="flex"
            alignItems="flex-start"
            gap={1}
            px={1.5}
            py={1}
            bgcolor="var(--yellow-10)"
            border="1px solid var(--yellow-25)"
            borderRadius="8px"
          >
            <WarningAmberRoundedIcon aria-hidden sx={{ fontSize: 18, color: "var(--yellow)", mt: "1px" }} />
            <Typography variant="body2" color="var(--dark)">
              {t("Onboarding-Confirm-Body")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" color="secondary" onClick={onBack} disabled={submitting}>
              {t("Onboarding-Confirm-Edit")}
            </Button>
            <Button fullWidth variant="contained" color="secondary" onClick={onConfirm} disabled={submitting}>
              {submitting ? <CircularProgress size={20} color="inherit" /> : t("Onboarding-Form-Submit")}
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};

// A silent refresh writes the rotated token to storage only, leaving React state holding a dead one.
const storedToken = (fallback?: string): string | undefined =>
  secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER)?.accessToken ?? fallback;

/** The organisation this card is answering for, carrying the way to a different one when the person has more than one. */
const OrganizationChip: React.FC<{ name: string; canSwitch: boolean }> = ({ name, canSwitch }) => {
  const { t } = useTranslation([common]);
  const navigate = useNavigate();
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      width="100%"
      boxSizing="border-box"
      // Matches the sector field beneath it, so the two read as one column of boxes rather than two sizes.
      height="40px"
      px={1.5}
      border="1px solid var(--gray2)"
      borderRadius="8px"
      aria-label={t("Onboarding-For-Organization", { organization: name })}
    >
      <BusinessOutlinedIcon fontSize="small" sx={{ color: "var(--gray4)" }} />
      {/* noWrap keeps a long legal name from turning the chip into a paragraph; the tooltip gives the whole name back. */}
      <Tooltip title={name} arrow>
        <Typography variant="h3" flex={1} noWrap>
          {name}
        </Typography>
      </Tooltip>
      {canSwitch && (
        // Green, not the body navy beside it: at rest this button has no box, so colour is what marks it as an action.
        <Button variant="text" color="primary" size="small" onClick={() => navigate("/select-organization")}>
          {t("Onboarding-Switch-Organization")}
        </Button>
      )}
    </Box>
  );
};

type AnswerRow = { label: string; value: string };

const AnswerRows: React.FC<{ rows: AnswerRow[] }> = ({ rows }) => (
  <>
    {rows.map((row) => (
      <Box key={row.label} display="flex" flexDirection="column" gap="2px">
        <Typography variant="body2" color="var(--gray9)">
          {row.label}
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {row.value}
        </Typography>
      </Box>
    ))}
  </>
);

// The grey frame both the bank's values and the borrower's own answers sit in, so the two read alike.
const AnswerBox: React.FC<{ heading: React.ReactNode; rows: AnswerRow[]; footer?: React.ReactNode; mb?: number }> = ({
  heading,
  rows,
  footer,
  mb,
}) => (
  <Box
    display="flex"
    flexDirection="column"
    gap="10px"
    mb={mb}
    px={2}
    py={1.75}
    bgcolor="var(--gray3)"
    border="1px solid var(--gray2)"
    borderRadius="8px"
  >
    {heading}
    <AnswerRows rows={rows} />
    {footer}
  </Box>
);

/** What the bank already set: context for the questions below, never an input. */
const BankProvided: React.FC<{ organization: Organization }> = ({ organization }) => {
  const { t, i18n } = useTranslation([common]);
  const { sectorIsic, sectorTsic, listingStatus } = organization;
  const rows = [
    sectorIsic && { label: t("Classification-Sector-Isic-Label"), value: sectorRefLabel(sectorIsic, i18n.language) },
    sectorTsic && { label: t("Classification-Sector-Tsic-Label"), value: sectorRefLabel(sectorTsic, i18n.language) },
    listingStatus && { label: t("Classification-Card-Listing-Status"), value: t(LISTING_STATUS_LABEL_KEY[listingStatus]) },
  ].filter((row): row is AnswerRow => Boolean(row));

  if (!rows.length) return null;
  return (
    <AnswerBox
      mb={3}
      rows={rows}
      heading={
        <Box display="flex" alignItems="center" gap={0.75}>
          <LockOutlinedIcon sx={{ fontSize: 14, color: "var(--gray9)" }} />
          <Typography variant="body2" fontWeight={600} color="var(--gray9)">
            {t("Onboarding-Bank-Provided")}
          </Typography>
        </Box>
      }
      footer={
        <Typography variant="body2" color="var(--gray9)">
          {t("Classification-Tell-Bank")}
        </Typography>
      }
    />
  );
};

type OnboardingFormProps = {
  organization: Organization;
  asked: AskedFields;
  // Answers carried over a reload, so a question still asked keeps what the borrower picked.
  carried?: OnboardingFormValues;
  bankFilled: OnboardingField[];
  onBankFilled: (fields: OnboardingField[], values: OnboardingFormValues) => void;
};

const OnboardingForm: React.FC<OnboardingFormProps> = ({ organization, asked, carried, bankFilled, onBankFilled }) => {
  const { t, i18n } = useTranslation([common]);
  const { user } = useAuth();
  const { finish, abandon } = useExit();

  const organizationRef = organization._id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrorKind, setSubmitErrorKind] = useState<SubmitErrorKind | null>(null);
  // the answers waiting on the borrower's confirm; null while the form is still being filled
  const [checking, setChecking] = useState<OnboardingFormValues | null>(null);

  const asks = (field: OnboardingField) => asked.missing.includes(field) || asked.optional.includes(field);
  const isRequired = (field: OnboardingField) => asked.missing.includes(field);

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm<OnboardingFormValues>({
    mode: "onChange",
    defaultValues: carried ?? { sectorIsicCode: "", sectorTsicCode: "", listingStatus: "" },
  });

  // react-hook-form stores each message at validation time, so a language switch leaves a stale one on screen.
  const erroredFields = useRef<(keyof OnboardingFormValues)[]>([]);
  erroredFields.current = Object.keys(errors) as (keyof OnboardingFormValues)[];
  const [langTick, setLangTick] = useState(0);

  useEffect(() => {
    const bump = () => setLangTick((n) => n + 1);
    i18n.on("languageChanged", bump);
    return () => {
      i18n.off("languageChanged", bump);
    };
  }, [i18n]);

  useEffect(() => {
    // Bumped state, not a direct trigger in the listener: this runs after the re-render, when the rules already carry the new language.
    if (langTick && erroredFields.current.length) trigger(erroredFields.current);
  }, [langTick, trigger]);

  // Once per mount: the hook refetches on any change to this value, which would reset the picker mid-form.
  const [sectorToken] = useState(() => storedToken(user?.accessToken));
  // No token, no fetch: a list for a question the bank already answered is never needed.
  const tsicState = useSectorOptions(asks("sectorTsicCode") ? sectorToken : undefined, "tsic");
  const sectorsLoading = tsicState.loading;
  // every list the picker loaded, so the answer check can name the code it shows
  const isicNames = useRef(new Map<string, SectorRef>());
  // ISIC is answered as a section, then one of its BOT codes, from the same list the bank picks from
  const loadIsicSectors = useCallback(
    (parentId?: string) =>
      SsoService.getOnboardingSectors(sectorToken ?? "", "isic", undefined, parentId).then((rows) => {
        rows.forEach((row) => isicNames.current.set(row.code, row));
        return rows.map((row) => ({ id: row.id, value: row.code, labelEn: row.nameEn, labelTh: row.nameTh }));
      }),
    [sectorToken],
  );
  const isicRequired = isRequired("sectorIsicCode");
  const isicRules = useMemo(
    () => (isicRequired ? { required: t("Onboarding-Form-Sector-Required") } : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- langTick rebuilds the message after a language switch
    [isicRequired, t, langTick],
  );

  const onSubmit = async (values: OnboardingFormValues) => {
    const token = storedToken(user?.accessToken);
    if (!token) return;
    setIsSubmitting(true);
    setSubmitErrorKind(null);
    // Only what was asked and answered; an optional field left empty is not sent.
    const payload: OnboardingPayload = { organizationRef };
    if (asks("sectorIsicCode") && values.sectorIsicCode) payload.sectorIsicCode = values.sectorIsicCode;
    if (asks("sectorTsicCode") && values.sectorTsicCode) payload.sectorTsicCode = values.sectorTsicCode;
    if (asks("listingStatus") && values.listingStatus) payload.listingStatus = values.listingStatus;
    try {
      await SsoService.submitOnboarding(token, payload);
      cacheOnboardingCompleted(organizationRef);
      finish();
    } catch (err) {
      const axiosErr = err as AxiosError<OnboardingErrorData>;
      if (axiosErr.response?.data?.code === "SSO_ONBOARDING_FIELD_ALREADY_SET") {
        onBankFilled(axiosErr.response.data.results?.already_set ?? [], values);
        return;
      }
      const kind = mapSubmitErrorKind(axiosErr.response?.data?.code);
      // The server has answered for this organisation, so record it or the banner nags straight back into a refused form.
      if (kind === "ALREADY_COMPLETED") cacheOnboardingCompleted(organizationRef);
      setSubmitErrorKind(kind);
      setIsSubmitting(false);
    }
  };

  const checkRows = (values: OnboardingFormValues): AnswerRow[] => {
    const isic = isicNames.current.get(values.sectorIsicCode);
    const tsic = tsicState.options.find((option) => option.value === values.sectorTsicCode);
    const th = i18n.language.includes("th");
    return [
      asks("sectorIsicCode") &&
        values.sectorIsicCode && {
          label: t("Classification-Sector-Isic-Label"),
          value: isic ? sectorRefLabel(isic, i18n.language) : values.sectorIsicCode,
        },
      asks("sectorTsicCode") &&
        values.sectorTsicCode && {
          label: t("Classification-Sector-Tsic-Label"),
          value: tsic ? `${tsic.value} — ${th ? tsic.nameTH : tsic.nameEN}` : values.sectorTsicCode,
        },
      asks("listingStatus") &&
        values.listingStatus && {
          label: t("Classification-Card-Listing-Status"),
          value: t(LISTING_STATUS_LABEL_KEY[values.listingStatus]),
        },
    ].filter((row): row is AnswerRow => Boolean(row));
  };

  const confirmAnswers = async () => {
    if (!checking) return;
    await onSubmit(checking);
    setChecking(null);
  };

  // Client-side only: the server keeps just pending and completed, so postponing has nothing to post.
  const notNow = () => {
    dismissReminder(organizationRef);
    abandon();
  };

  const refusedKind = submitErrorKind && TERMINAL_ERRORS.includes(submitErrorKind) ? submitErrorKind : null;
  const organizationCount = user?.organizationsRef?.length ?? 0;
  const bankFilledNames = new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(
    ONBOARDING_FIELDS.filter((f) => bankFilled.includes(f)).map((f) => t(FIELD_NAME_KEY[f]))
  );

  return (
    <AuthPlate>
      <Box component="form" onSubmit={handleSubmit((values) => setChecking(values))} width="100%">
        <Stack direction="column" spacing={2} width="100%" mb={3}>
          <Typography variant="h1">{t("Onboarding-Title")}</Typography>
          {/* The answer is written against this organisation, so the card has to say which one it means. */}
          <OrganizationChip name={organization.name} canSwitch={organizationCount > 1} />
          <Typography variant="body1">{t("Onboarding-Subtitle")}</Typography>
        </Stack>

        {bankFilled.length > 0 && (
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: "8px" }}>
            {t("Onboarding-Bank-Filled-Notice", { fields: bankFilledNames })}
          </Alert>
        )}

        <BankProvided organization={organization} />

        {submitErrorKind && !refusedKind && (
          <Typography variant="text2" display="block" mb={2} style={{ color: "var(--red)" }}>
            {t(ERROR_KEY[submitErrorKind])}
          </Typography>
        )}

        <Stack direction="column" spacing={2.5} width="100%">
          {asks("sectorIsicCode") && (
            <SectorIsicPicker
              name="sectorIsicCode"
              control={control}
              value={watch("sectorIsicCode")}
              // clearing for a newly picked section is not an answer yet, so it is not flagged as one
              onChange={(code) => setValue("sectorIsicCode", code, { shouldValidate: code !== "" })}
              label={t("Classification-Sector-Isic-Label")}
              required={isicRequired}
              loadSectors={loadIsicSectors}
              rules={isicRules}
              error={!!errors.sectorIsicCode}
              errorMessage={errors.sectorIsicCode?.message}
              unavailableMessage={t("Onboarding-Form-Sector-Unavailable")}
            />
          )}

          {asks("listingStatus") && (
            <Box>
              <Box mb={0.5}>
                <LabelInput label={t("Onboarding-Form-Listing-Status-Label")} required={isRequired("listingStatus")} />
              </Box>
              <Controller
                control={control}
                name="listingStatus"
                rules={isRequired("listingStatus") ? { required: t("Onboarding-Form-Listing-Status-Required") } : undefined}
                render={({ field, fieldState }) => (
                  <>
                    <RadioGroup
                      row
                      aria-label="listingStatus"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <FormControlLabel
                        value="listed"
                        control={<Radio />}
                        label={t("Onboarding-Form-Listing-Status-Listed")}
                      />
                      <FormControlLabel
                        value="unlisted"
                        control={<Radio />}
                        label={t("Onboarding-Form-Listing-Status-Unlisted")}
                      />
                    </RadioGroup>
                    {fieldState.error?.message && (
                      <Typography variant="body2" color="error" data-testid="err-listingStatus">
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Box>
          )}

          {asks("sectorTsicCode") && (
            <SectorPicker
              {...tsicState}
              control={control}
              name="sectorTsicCode"
              label={t("Classification-Sector-Tsic-Label")}
              requiredMessage={isRequired("sectorTsicCode") ? t("Onboarding-Form-Sector-Required") : undefined}
            />
          )}
        </Stack>

        <Stack direction="column" spacing={1.5} width="100%" mt="30px">
          {/* --gray9, not --gray4: at 12px the lighter grey falls under 4.5:1 contrast. */}
          <Typography variant="body2" color="var(--gray9)" textAlign="center">
            {t("Onboarding-Final-Note")}
          </Typography>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            disabled={!isValid || isSubmitting || sectorsLoading}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t("Onboarding-Form-Submit")}
          </Button>
          {/* Onboarding never hard-gates the app, so leaving without answering has to stay possible. */}
          <Button fullWidth variant="text" color="secondary" onClick={notNow} disabled={isSubmitting}>
            {t("Onboarding-Not-Now")}
          </Button>
        </Stack>
      </Box>
      {checking && (
        <AnswerCheck
          rows={checkRows(checking)}
          submitting={isSubmitting}
          onBack={() => setChecking(null)}
          onConfirm={confirmAnswers}
        />
      )}
      {refusedKind && <RefusalModal kind={refusedKind} onLeave={abandon} />}
    </AuthPlate>
  );
};

/** Outside `Layout` there is no OrganizationProvider, so the page resolves its own organisation and its answer. */
const useOnboardingTarget = () => {
  const { user } = useAuth();
  // Not a dependency: re-running would restart the decision underneath a filled-in form.
  const fallbackToken = useRef(user?.accessToken);
  const [organization, setOrganization] = useState<Organization>();
  const [asked, setAsked] = useState<AskedFields>();
  const [state, setState] = useState<OnboardingGateState>("deciding");
  // Bumped only by an explicit reload, so nothing else restarts the decision underneath a filled-in form.
  const [round, setRound] = useState(0);

  useEffect(() => {
    let active = true;
    setState("deciding");

    const decide = async () => {
      let org: Organization | undefined;
      try {
        // Own card, not the interceptor's dialog: one failure should not raise two messages.
        const response = await apiClient.get("/v2/organizations", { _suppressGlobalAlert: true } as any);
        org = response.data?.results?.organization;
      } catch {
        org = undefined;
      }
      if (!active) return;
      // Without the organisation there is no question to ask, so the gate terminates rather than waiting forever.
      if (!org?._id) {
        setState("error");
        return;
      }
      setOrganization(org);
      if (isOnboardingCompletedCached(org._id)) {
        setState("completed");
        return;
      }
      // Read here, not at mount: the organisation fetch above is what triggers a 401 refresh, and that refresh
      // writes the rotated token to storage only.
      const token = storedToken(fallbackToken.current);
      if (!token) {
        setState("error");
        return;
      }
      try {
        const result = await SsoService.getOnboardingStatus(token, org._id);
        if (!active) return;
        if (result.onboarding_status === "completed") {
          cacheOnboardingCompleted(org._id);
          setState("completed");
          return;
        }
        // Pending without its lists is a server this page cannot read, and an empty form would look broken.
        if (!Array.isArray(result.missing_fields)) {
          setState("error");
          return;
        }
        setAsked({ missing: result.missing_fields, optional: result.optional_fields ?? [] });
        setState("pending");
      } catch {
        if (!active) return;
        setState("error");
      }
    };

    decide();

    return () => {
      active = false;
    };
  }, [round]);

  // Deciding in the caller's batch unmounts the form before the re-read starts, so no reload keeps it stuck submitting.
  const reload = () => {
    setState("deciding");
    setRound((n) => n + 1);
  };

  return { state, organization, asked, reload };
};

const OnboardingGate: React.FC = () => {
  const { state, organization, asked, reload } = useOnboardingTarget();
  const [bankFilled, setBankFilled] = useState<OnboardingField[]>([]);
  const [carried, setCarried] = useState<OnboardingFormValues>();

  // The bank filled a field between the read and the answer: re-read both, then ask only what is left.
  const onBankFilled = (fields: OnboardingField[], values: OnboardingFormValues) => {
    setBankFilled(fields);
    setCarried(values);
    reload();
  };

  if (state === "deciding") return <CircularProgress />;
  if (state === "error") {
    return (
      <ExitCard titleKey="Onboarding-Title-Unavailable" messageKey="Onboarding-Status-Unavailable" severity="error" />
    );
  }
  if (state === "pending" && organization?._id && asked) {
    return (
      <OnboardingForm
        organization={organization}
        asked={asked}
        carried={carried}
        bankFilled={bankFilled}
        onBankFilled={onBankFilled}
      />
    );
  }
  if (state === "completed") {
    return <ExitCard titleKey="Onboarding-Title-Nothing-To-Do" messageKey="Onboarding-Nothing-To-Do" severity="info" />;
  }
  // A state no branch above claimed must not reassure the user that there is nothing to do.
  return <ExitCard titleKey="Onboarding-Title-Unavailable" messageKey="Onboarding-Status-Unavailable" severity="error" />;
};

const OnboardingPage: React.FC = () => {
  const { organization: sessionOrg, isInitialized } = useAuth();

  // GET /v2/organizations sends the selected organisation as a required header, so asking without one is a 400.
  if (!isInitialized) return <CircularProgress />;
  if (!sessionOrg) return <Navigate to="/select-organization" replace />;

  // disableAutoRedirect → this page owns navigation on the way out; LoginLayout's own would race it.
  return (
    <LoginLayout heroCue disableAutoRedirect>
      <OnboardingGate />
    </LoginLayout>
  );
};

export default OnboardingPage;
